import { Viewport, MIN_BAR_SPACING, MAX_BAR_SPACING } from "./Viewport";
import { Theme } from "./theme";
import { TimeframeStore } from "../data/DataStore";
import { drawCandles, drawVolume } from "./renderer/CandleRenderer";
import { drawGrid } from "./renderer/GridRenderer";
import { drawPriceAxis } from "./renderer/PriceAxis";
import { drawTimeAxis } from "./renderer/TimeAxis";
import { drawCrosshair } from "./renderer/Crosshair";
import { drawIndicators } from "./renderer/IndicatorPane";
import { drawDSZones } from "./renderer/DSZoneRenderer";
import { DSZoneEngine } from "./indicators/DSzone";
import { easeInOut, clamp } from "./util";
import type { DrawingLayer } from "../drawings/DrawingLayer";

export interface CursorState {
  x: number; // canvas px
  y: number;
  inPlot: boolean; // within the price pane
  inChart: boolean; // within price pane + indicator pane
  active: boolean; // pointer is over the chart at all
}

export type CrosshairMode = "normal" | "hidden";

interface Anim {
  active: boolean;
  start: number;
  dur: number;
  fromFirst: number;
  toFirst: number;
  fromSpacing: number;
  toSpacing: number;
}

export class Chart {
  readonly container: HTMLElement;
  readonly viewport = new Viewport();

  baseCanvas: HTMLCanvasElement;
  overlayCanvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private octx: CanvasRenderingContext2D;

  dpr = Math.max(1, window.devicePixelRatio || 1);
  cssWidth = 0;
  cssHeight = 0;

  priceAxisWidth = 70;
  timeAxisHeight = 26;
  indicatorHeight = 96; // bottom pane for ATR/TR
  showIndicators = true;
  showDSZone = true;
  dszEngine: DSZoneEngine | null = null;

  // Replay: when >= 0, the chart behaves as if the dataset ends at index (replayCap-1).
  // Everything downstream (visible range, autoscale, last price, crosshair, DSzone,
  // indicators) must treat replayCap as the bar count so future candles never appear.
  replayCap = -1;
  // extra renderers drawn on the base canvas AFTER the axes (order lines, trade markers)
  baseExtra: Array<(ctx: CanvasRenderingContext2D, chart: Chart) => void> = [];

  data: TimeframeStore | null = null;
  tfSeconds = 60;
  priceDecimals = 2;

  cursor: CursorState = { x: 0, y: 0, inPlot: false, inChart: false, active: false };
  crosshairMode: CrosshairMode = "normal";
  showVolume = true;

  drawingLayer: DrawingLayer | null = null;

  // hovered bar index (snapped), -1 if none
  hoverIndex = -1;

  private needsBase = true;
  private needsOverlay = true;
  private rafId = 0;
  private anim: Anim = {
    active: false,
    start: 0,
    dur: 0,
    fromFirst: 0,
    toFirst: 0,
    fromSpacing: 0,
    toSpacing: 0,
  };

  // callbacks
  onViewportChange: (() => void) | null = null;
  onCrosshairMove: (() => void) | null = null;

  private ro: ResizeObserver;
  private lastDataRange: [number, number] = [-1, -1];
  private sized = false;

  constructor(container: HTMLElement) {
    this.container = container;
    this.baseCanvas = document.createElement("canvas");
    this.overlayCanvas = document.createElement("canvas");
    for (const c of [this.baseCanvas, this.overlayCanvas]) {
      c.style.position = "absolute";
      c.style.inset = "0";
      c.style.width = "100%";
      c.style.height = "100%";
      container.appendChild(c);
    }
    this.overlayCanvas.style.pointerEvents = "none";
    this.ctx = this.baseCanvas.getContext("2d")!;
    this.octx = this.overlayCanvas.getContext("2d")!;

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(container);
    this.onWinResize = () => this.resize();
    window.addEventListener("resize", this.onWinResize);
    this.resize();
    this.loop();
  }

  setDrawingLayer(layer: DrawingLayer) {
    this.drawingLayer = layer;
  }

  resize() {
    const rect = this.container.getBoundingClientRect();
    this.cssWidth = Math.max(1, Math.floor(rect.width));
    this.cssHeight = Math.max(1, Math.floor(rect.height));
    this.dpr = Math.max(1, window.devicePixelRatio || 1);
    for (const c of [this.baseCanvas, this.overlayCanvas]) {
      c.width = Math.floor(this.cssWidth * this.dpr);
      c.height = Math.floor(this.cssHeight * this.dpr);
    }
    this.updatePlot();
    // Once we first get a real size, (re)apply the default view — the initial
    // timeframe load may have run while the host still had zero width.
    if (!this.sized && this.plotWidth > 0) {
      this.sized = true;
      if (this.data) this.applyDefaultView();
    } else if (this.sized && this.data) {
      this.refreshData();
    }
    this.markDirty();
  }

  applyDefaultView() {
    if (!this.data) return;
    this.viewport.barCount = this.effBarCount; // honor the replay/practice cap
    this.viewport.barSpacing = clamp(this.plotWidth / 200, MIN_BAR_SPACING, MAX_BAR_SPACING);
    this.viewport.scrollToRight();
    this.viewport.clampScroll();
    this.viewport.priceAuto = true;
    this.refreshData(true);
    this.emitViewport();
  }

  updatePlot() {
    const indH = this.showIndicators ? this.indicatorHeight : 0;
    this.viewport.setPlot(0, 0, this.cssWidth - this.priceAxisWidth, this.cssHeight - this.timeAxisHeight - indH);
  }

  // bottom indicator pane rect (price pane sits above it)
  get indicatorRect() {
    return { top: this.viewport.plot.height, height: this.showIndicators ? this.indicatorHeight : 0, width: this.viewport.plot.width };
  }
  // full drawable height (price pane + indicator pane), used by crosshair
  get fullPlotHeight() {
    return this.viewport.plot.height + (this.showIndicators ? this.indicatorHeight : 0);
  }

  get plotWidth() {
    return this.viewport.plot.width;
  }
  get plotHeight() {
    return this.viewport.plot.height;
  }

  // effective bar count honoring the replay cap
  get effBarCount() {
    if (this.replayCap >= 0) return this.replayCap;
    return this.data ? this.data.barCount : 0;
  }
  get lastVisibleIndex() {
    return this.effBarCount - 1;
  }

  // Set the replay cap (number of revealed bars). Keeps the view pinned to the
  // right edge when the user is already watching the live edge.
  setReplayCap(cap: number, stickRight = true) {
    const wasAtRight = this.viewport.firstIndex + this.viewport.barsVisible >= this.viewport.barCount - 1;
    this.replayCap = cap;
    this.viewport.barCount = this.effBarCount;
    if (stickRight && wasAtRight) {
      this.viewport.scrollToRight();
    }
    this.viewport.clampScroll();
    this.autoScalePrice();
    this.refreshData();
    this.markDirty();
    this.emitViewport();
  }

  markDirty() {
    this.needsBase = true;
  }
  markOverlay() {
    this.needsOverlay = true;
  }

  // ---- data / timeframe ----
  async setTimeframe(store: TimeframeStore, tfSeconds: number, keepCenterTime = true, replayTime?: number | null) {
    const prevCenterTime = this.data && keepCenterTime ? this.centerTime() : null;
    const prevBarsVisible = this.viewport.barsVisible;
    const prevTfSeconds = this.tfSeconds;

    this.data = store;
    this.tfSeconds = tfSeconds;
    this.dszEngine = new DSZoneEngine(store, tfSeconds);
    // apply replay cap for the new timeframe BEFORE positioning so future never flashes
    if (replayTime != null) {
      const idx = await store.lastIndexAtOrBefore(replayTime - tfSeconds);
      this.replayCap = idx + 1;
    } else {
      this.replayCap = -1;
    }
    this.viewport.barCount = this.effBarCount;

    if (prevCenterTime != null && prevBarsVisible > 0) {
      // preserve visible duration -> pick barSpacing so bars*tf ~= duration
      const durationSec = prevBarsVisible * prevTfSeconds;
      const newBars = clamp(durationSec / tfSeconds, 20, 100000);
      this.viewport.barSpacing = clamp(this.plotWidth / newBars, MIN_BAR_SPACING, MAX_BAR_SPACING);
      const centerIdx = await store.timeToIndex(prevCenterTime);
      this.viewport.firstIndex = centerIdx - this.viewport.barsVisible / 2;
    } else {
      // default: show the most recent ~200 bars
      this.viewport.barSpacing = clamp(this.plotWidth / 200, MIN_BAR_SPACING, MAX_BAR_SPACING);
      this.viewport.scrollToRight();
    }
    this.viewport.clampScroll();
    await this.refreshData(true);
    this.markDirty();
    this.emitViewport();
  }

  centerTime(): number | null {
    if (!this.data) return null;
    const idx = Math.round(clamp(this.viewport.centerIndex, 0, this.lastVisibleIndex));
    const t = this.data.timeAt(idx);
    return isFinite(t) ? t : null;
  }

  // ---- time <-> coordinate mapping for drawings (anchored by absolute time) ----
  // Fractional bar index for a unix-seconds timestamp. Exact when the containing
  // chunk is resident; otherwise linearly approximated from chunk metadata.
  indexOfTime(t: number): number {
    const data = this.data;
    if (!data) return 0;
    const chunks = data.meta.chunks;
    if (chunks.length === 0) return 0;
    if (t <= chunks[0].startTime) return 0;
    const lastCh = chunks[chunks.length - 1];
    if (t >= lastCh.endTime) return data.barCount - 1;
    // binary search chunk by time span
    let lo = 0,
      hi = chunks.length - 1,
      ci = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const ch = chunks[mid];
      if (t < ch.startTime) hi = mid - 1;
      else if (t > ch.endTime) lo = mid + 1;
      else {
        ci = mid;
        break;
      }
      ci = Math.min(mid, chunks.length - 1);
    }
    if (t < chunks[ci].startTime && ci > 0) ci -= 1;
    const ch = chunks[ci];
    const base = ci * data.chunkSize;
    if (data.isChunkLoaded(ci)) {
      // exact fractional index via within-chunk search
      let a = base,
        b = base + ch.count - 1;
      const ta = data.timeAt(a);
      const tb = data.timeAt(b);
      if (t <= ta) return a;
      if (t >= tb) return b;
      while (b - a > 1) {
        const mid = (a + b) >> 1;
        const tm = data.timeAt(mid);
        if (tm < t) a = mid;
        else b = mid;
      }
      const t0 = data.timeAt(a);
      const t1 = data.timeAt(b);
      const frac = t1 > t0 ? (t - t0) / (t1 - t0) : 0;
      return a + frac;
    }
    // approximate within chunk span
    const span = ch.endTime - ch.startTime || 1;
    return base + ((t - ch.startTime) / span) * (ch.count - 1);
  }

  xOfTime(t: number): number {
    return this.viewport.xCenterOfIndex(this.indexOfTime(t));
  }

  // snapped bar time for a plot-relative x (nearest bar). Falls back to a synthetic
  // extrapolated time when x is beyond loaded data so off-data drawing still works.
  timeOfX(px: number): number {
    const data = this.data;
    if (!data) return 0;
    const idxF = this.viewport.indexOfX(px);
    // floor (not round) so this is the exact inverse of xOfTime (which centers on
    // the bar) and matches barIndexAtX — otherwise dragged anchors drift a bar.
    const idx = Math.floor(idxF);
    if (idx >= 0 && idx < data.barCount) {
      const t = data.timeAt(idx);
      if (isFinite(t)) return t;
    }
    // extrapolate using tf seconds from the nearest edge bar
    const edge = clamp(idx, 0, data.barCount - 1);
    const te = data.timeAt(edge);
    if (isFinite(te)) return te + (idx - edge) * this.tfSeconds;
    return idx * this.tfSeconds;
  }

  // ensure visible chunks are loaded; recompute autoscale
  async refreshData(force = false) {
    if (!this.data) return;
    const [lo, hi] = this.viewport.visibleRange();
    if (!force && lo === this.lastDataRange[0] && hi === this.lastDataRange[1] && this.data.hasRange(lo, hi)) {
      return;
    }
    this.lastDataRange = [lo, hi];
    const hadRange = this.data.hasRange(lo, hi);
    if (!hadRange) {
      await this.data.ensureRange(lo, hi, 1);
      this.markDirty();
    } else {
      // prefetch neighbors without blocking
      this.data.ensureRange(lo, hi, 1);
    }
    this.autoScalePrice();
    this.markDirty();
    this.emitViewport();
  }

  autoScalePrice() {
    if (!this.data || !this.viewport.priceAuto) return;
    const [lo, hi] = this.viewport.visibleRange();
    let min = Infinity;
    let max = -Infinity;
    for (let i = lo; i < hi; i++) {
      const l = this.data.lowAt(i);
      const h = this.data.highAt(i);
      if (isFinite(l) && l < min) min = l;
      if (isFinite(h) && h > max) max = h;
    }
    // include horizontal drawings that add price levels? keep simple: candles only.
    if (!isFinite(min) || !isFinite(max)) return;
    if (min === max) {
      min -= 1;
      max += 1;
    }
    const pad = (max - min) * 0.12;
    this.viewport.priceMin = min - pad;
    this.viewport.priceMax = max + pad * 1.4; // extra top room for legend
  }

  // ---- interaction helpers ----
  updateCursor(x: number, y: number, active: boolean) {
    this.cursor.x = x;
    this.cursor.y = y;
    this.cursor.active = active;
    this.cursor.inPlot = active && x >= 0 && x <= this.plotWidth && y >= 0 && y <= this.plotHeight;
    this.cursor.inChart = active && x >= 0 && x <= this.plotWidth && y >= 0 && y <= this.fullPlotHeight;
    const prevHover = this.hoverIndex;
    if (this.cursor.inChart && this.data) {
      const idx = this.viewport.barIndexAtX(x);
      this.hoverIndex = idx >= 0 && idx < this.effBarCount ? idx : -1;
    } else {
      this.hoverIndex = -1;
    }
    // the ATR/TR readout lives on the base layer — repaint it when the hovered candle changes
    if (this.hoverIndex !== prevHover && this.showIndicators) this.markDirty();
    this.markOverlay();
    if (this.onCrosshairMove) this.onCrosshairMove();
  }

  emitViewport() {
    if (this.onViewportChange) this.onViewportChange();
  }

  // zoom around a plot-relative anchor x (keeps bar under anchor fixed)
  zoomBy(factor: number, anchorX: number) {
    const vp = this.viewport;
    const anchorIndex = vp.indexOfX(anchorX);
    vp.barSpacing = clamp(vp.barSpacing * factor, MIN_BAR_SPACING, MAX_BAR_SPACING);
    vp.firstIndex = anchorIndex - anchorX / vp.barSpacing;
    vp.clampScroll();
    this.afterViewportInteraction();
  }

  panByBars(dBars: number) {
    this.viewport.firstIndex += dBars;
    this.viewport.clampScroll();
    this.afterViewportInteraction();
  }

  panByPixels(dxPx: number) {
    this.viewport.firstIndex -= dxPx / this.viewport.barSpacing;
    this.viewport.clampScroll();
    this.afterViewportInteraction();
  }

  // vertical price drag (manual scale)
  scalePriceByDrag(dyPx: number, anchorY: number) {
    const vp = this.viewport;
    vp.priceAuto = false;
    const anchorPrice = vp.priceOfY(anchorY);
    const factor = Math.exp(dyPx * 0.005);
    const newMin = anchorPrice - (anchorPrice - vp.priceMin) * factor;
    const newMax = anchorPrice + (vp.priceMax - anchorPrice) * factor;
    vp.priceMin = newMin;
    vp.priceMax = newMax;
    this.markDirty();
  }

  shiftPrice(dyPx: number) {
    const vp = this.viewport;
    vp.priceAuto = false;
    const span = vp.priceMax - vp.priceMin;
    const dPrice = (dyPx / this.plotHeight) * span;
    vp.priceMin += dPrice;
    vp.priceMax += dPrice;
    this.markDirty();
  }

  resetPriceScale() {
    this.viewport.priceAuto = true;
    this.autoScalePrice();
    this.markDirty();
  }

  afterViewportInteraction() {
    this.autoScalePrice();
    this.markDirty();
    this.emitViewport();
    this.refreshData();
  }

  // ---- animated navigation ----
  animateTo(targetFirstIndex: number, targetSpacing: number, dur = 420) {
    this.anim.active = true;
    this.anim.start = performance.now();
    this.anim.dur = dur;
    this.anim.fromFirst = this.viewport.firstIndex;
    this.anim.toFirst = targetFirstIndex;
    this.anim.fromSpacing = this.viewport.barSpacing;
    this.anim.toSpacing = clamp(targetSpacing, MIN_BAR_SPACING, MAX_BAR_SPACING);
  }

  async goToIndex(index: number, opts: { spacing?: number; animate?: boolean } = {}) {
    if (!this.data) return;
    const spacing = opts.spacing ?? Math.max(this.viewport.barSpacing, 6);
    const barsVisible = this.plotWidth / spacing;
    const targetFirst = index - barsVisible / 2;
    // preload target region
    await this.data.ensureRange(Math.max(0, targetFirst), targetFirst + barsVisible, 1);
    if (opts.animate === false) {
      this.viewport.barSpacing = spacing;
      this.viewport.firstIndex = targetFirst;
      this.viewport.clampScroll();
      this.afterViewportInteraction();
    } else {
      this.animateTo(targetFirst, spacing);
    }
  }

  fitToData() {
    if (!this.data) return;
    this.viewport.barSpacing = clamp(this.plotWidth / Math.min(this.data.barCount, 300), MIN_BAR_SPACING, MAX_BAR_SPACING);
    this.viewport.scrollToRight();
    this.viewport.priceAuto = true;
    this.afterViewportInteraction();
  }

  resetChart() {
    if (!this.data) return;
    this.viewport.barSpacing = clamp(this.plotWidth / 200, MIN_BAR_SPACING, MAX_BAR_SPACING);
    this.viewport.scrollToRight();
    this.viewport.priceAuto = true;
    this.afterViewportInteraction();
  }

  // ---- render loop ----
  private onWinResize: () => void = () => {};

  private loop = () => {
    this.rafId = requestAnimationFrame(this.loop);
    // Self-heal: some environments don't deliver ResizeObserver notifications
    // reliably. Cheaply detect a size change and re-sync.
    if (this.container.clientWidth !== this.cssWidth || this.container.clientHeight !== this.cssHeight) {
      if (this.container.clientWidth > 0) this.resize();
    }
    if (this.anim.active) {
      const t = clamp((performance.now() - this.anim.start) / this.anim.dur, 0, 1);
      const e = easeInOut(t);
      this.viewport.firstIndex = this.anim.fromFirst + (this.anim.toFirst - this.anim.fromFirst) * e;
      this.viewport.barSpacing = this.anim.fromSpacing + (this.anim.toSpacing - this.anim.fromSpacing) * e;
      this.viewport.clampScroll();
      this.autoScalePrice();
      this.markDirty();
      if (t >= 1) {
        this.anim.active = false;
        this.emitViewport();
        this.refreshData();
      }
    }
    if (this.needsBase) {
      this.drawBase();
      this.needsBase = false;
      this.needsOverlay = true;
    }
    if (this.needsOverlay) {
      this.drawOverlay();
      this.needsOverlay = false;
    }
  };

  private drawBase() {
    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.cssWidth, this.cssHeight);
    ctx.fillStyle = Theme.bg;
    ctx.fillRect(0, 0, this.cssWidth, this.cssHeight);

    drawGrid(ctx, this);
    if (this.showDSZone) drawDSZones(ctx, this);
    if (this.showVolume) drawVolume(ctx, this);
    drawCandles(ctx, this);
    if (this.drawingLayer) this.drawingLayer.render(ctx, this);
    if (this.showIndicators) drawIndicators(ctx, this);
    drawPriceAxis(ctx, this);
    drawTimeAxis(ctx, this);
    for (const fn of this.baseExtra) fn(ctx, this);
    ctx.restore();
  }

  private drawOverlay() {
    const octx = this.octx;
    octx.save();
    octx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    octx.clearRect(0, 0, this.cssWidth, this.cssHeight);
    if (this.drawingLayer) this.drawingLayer.renderOverlay(octx, this);
    if (this.crosshairMode !== "hidden") drawCrosshair(octx, this);
    octx.restore();
  }

  destroy() {
    cancelAnimationFrame(this.rafId);
    this.ro.disconnect();
    window.removeEventListener("resize", this.onWinResize);
  }
}
